// Shortlist action tracking — `shortlist.alert-tracking.service.ng1.ts` (`ShortlistAlertTrackingService`),
// `extra.info.builder.ng1.ts` (`ExtraInfoBuilder`), and the bulk-tracking helpers the candidate and
// contact list components each keep a private copy of (`trackCandidateBulkAction`,
// `trackClientContactBulkAction`). Paths below are relative to `careero/web/chrome-extension/` unless
// they start with `common/`; `SP/` is `chrome-extension/shortlist-poc/`.
//
// ⚠️ EVERY STRING IN THIS FILE IS AN ANALYTICS CONTRACT. The event names, the category, the
// extraInfo keys and their ORDER (the wire format is a flat `k@@@v@@@k@@@v` string, so order is
// data) are what careero's BI importers read. Copy upstream; never tidy.
//
// ⚠️ AND SO IS THE PAGE URL. Upstream posts `basePath + $location.url()` with every event
// (`common/services/internal-analytics.ng1.ts:60`, `:65`), and `WooActionTrackingDataImporter` /
// `ActionTrackingHelperService` prefix-match it (CLAUDE.md constraint 1). `trackingPageUrl()` is the
// ONLY place this port builds it, from the SPA's own location — whose routes are upstream's ui-router
// URLs verbatim — so it never invents a path, and in particular can never produce the dormant
// `…/review/snippet?` prefixes (`packages/protocol/test/url-contracts.test.ts`).
//
// Tracking is FIRE-AND-FORGET: every `track*` function here returns void, never throws, never
// awaits the network before returning, and swallows the request's failure. Call it and carry on.
import { trackEventWithUrl, trackEventWithUrlBulk, type TrackEventWithUrl } from '@woo-ext/api'
import { ViewMode } from '@woo-ext/protocol'

/** `ShortlistAlertHelperService.SHORTLIST_ANALYTICS_CATEGORY` (`SP/services/shortlist.alert-helper.service.ng1.ts:103`). Capital S — the specs' mocks say `'shortlist'`, the service does not. */
export const SHORTLIST_ANALYTICS_CATEGORY = 'Shortlist'

/** `ExtraInfoBuilder.delimiter` (`common/analytics/extra.info.builder.ng1.ts:2`). */
export const EXTRA_INFO_DELIMITER = '@@@'

/**
 * Candidate-side event names, verbatim. Where two upstream fields carry the same string (the list
 * component and the add-to-job service both declare `AddToJobClicked`) it appears once.
 */
export const CandidateAnalyticsEvent = {
  // SP/services/shortlist.candidate-actions.service.ng1.ts:46-48 (ShortlistCandidateActionsService)
  ViewInAts: 'ViewInAtsClicked',
  DownloadWooProfile: 'DownloadWooProfileClicked',
  EmailCandidateDetails: 'EmailCandidateDetailsClicked',
  // :117-121 (ShortlistHunterCandidateActionsService)
  AssignToJobInAts: 'AssignToJobInAtsClicked',
  Hide: 'HideClicked',
  Unhide: 'UnhideClicked',
  Favorite: 'FavoriteClicked',
  Unfavorite: 'UnfavoriteClicked',
  // SP/services/shortlist.candidate-add-to-job.service.ng1.ts:30-33 (and the list component's :266-271)
  AddToJob: 'AddToJobClicked',
  AddToJobBulk: 'AddToJobBulkClicked',
  CandidateAddedToJobBulk: 'CandidateAddedToAtsJobDetails',
  CandidateAddedToJob: 'CandidateAddedToAtsJob',
  // SP/extension-alert-container/extension-alert-candidates/extension-alert-candidates.component.ng1.ts:248-277
  SingleSelect: 'CandidateSingleSelectClicked',
  Multiselect: 'CandidateMultiselectClicked',
  FilterRelevancyDate: 'FilterCandidatesByRelevancyDateClicked',
  FilterShownAll: 'FilterShownAllCandidatesClicked',
  FilterShownFavorites: 'FilterShownFavoritesCandidatesClicked',
  // :253 — 'Unviewed' reused the Favorites event until 2026-09 (HS 48247321486).
  FilterShownUnviewed: 'FilterShownUnviewedCandidatesClicked',
  FilterShownArchived: 'FilterShownArchivedCandidatesClicked',
  FilterShownExported: 'FilterShownExportedCandidatesClicked',
  FilterShownMessaged: 'FilterShownMessagedCandidatesClicked',
  FilterShownActionedAndUnactioned: 'FilterShownActionedAndUnactionedCandidatesClicked',
  PreviewResume: 'PreviewResumeClicked',
  HideBulk: 'HideBulkClicked',
  FavoriteBulk: 'FavoriteBulkClicked',
  /** `analyticsExportedEvent` AND `analyticsExportClickedEvent` — the same string, two fields (:262, :268). */
  Export: 'ExportClicked',
  CandidateExported: 'CandidateExported',
  Message: 'MessageClicked',
  MessageBulk: 'MessageBulkClicked',
  ExportBulk: 'ExportBulkClicked',
  SearchStatusViewCandidates: 'SearchStatusViewCandidatesClicked',
  SearchStatusEditSearchCriteria: 'SearchStatusEditSearchCriteriaClicked',
  ExportToCsv: 'ExportToCsvClicked',
  ExportToCsvBulk: 'ExportToCsvBulkClicked',
  AddToAtsAutomation: 'AddToAtsAutomationClicked',
  AddToAtsAutomationBulk: 'AddToAtsAutomationBulkClicked',
  /** Not a field: a literal in `trackHunterResultsLoaded` (:2256), sent with its own JSON extraInfo. */
  HunterResultsLoaded: 'HunterResultsLoaded',
  // SP/extension-alert-candidate-snippet/extension-alert-candidate-snippet.component.ng1.ts:153-156
  SocialUrl: 'SocialUrlClicked',
  ProfileScroll: 'CandidateProfileScroll',
  /** Also `SP/services/shortlist.report-enrichment.service.ng1.ts:33`, which declares it and never sends it. */
  ReportEnrichment: 'ReportEnrichmentClicked',
  // SP/extension-alert-container/extension-alert-candidates/extension-alert-candidates-message/extension-alert-candidates-messages.component.ng1.ts:181
  MessageSendButton: 'CandidateMessageSendButtonClicked',
  // …/extension-alert-candidates-message/content/extension-alert-candidates-message-content.component.ng1.ts:80-81
  MessageCreateFollowup: 'CandidateMessageCreateFollowupClicked',
  MessageCreateFollowupFromProfile: 'CandidateMessageCreateFollowupFromCandidateProfileClicked',
} as const
export type CandidateAnalyticsEvent = (typeof CandidateAnalyticsEvent)[keyof typeof CandidateAnalyticsEvent]

/** Client-contact event names, verbatim. Same rules as `CandidateAnalyticsEvent`. */
export const ClientContactAnalyticsEvent = {
  // SP/extension-client-contact/services/client-contact-actions.service.ng1.ts:48, :77-81
  ViewInAts: 'ClientContactViewInAtsClicked',
  AssignToJobInAts: 'ClientContactAssignToJobInAtsClicked',
  Hide: 'ClientContactHideClicked',
  Unhide: 'ClientContactUnhideClicked',
  Favorite: 'ClientContactFavoriteClicked',
  Unfavorite: 'ClientContactUnfavoriteClicked',
  // SP/extension-client-contact/extension-alert-client-contacts/extension-alert-client-contacts.component.ng1.ts:227-250
  SingleSelect: 'ClientContactSingleSelectClicked',
  Multiselect: 'ClientContactMultiselectClicked',
  FilterRelevancyDate: 'FilterClientContactsByRelevancyDateClicked',
  FilterShownAll: 'FilterShownAllClientContactsClicked',
  FilterShownFavorites: 'FilterShownFavoritesClientContactsClicked',
  // :232 — 'Unviewed' reused the Favorites event until 2026-09 (HS 48247321486).
  FilterShownUnviewed: 'FilterShownUnviewedClientContactsClicked',
  FilterShownArchived: 'FilterShownArchivedClientContactsClicked',
  FilterShownExported: 'FilterShownExportedClientContactsClicked',
  FilterShownMessaged: 'FilterShownMessagedClientContactsClicked',
  FilterShownActionedAndUnactioned: 'FilterShownActionedAndUnactionedClientContactsClicked',
  /** Declared; its only caller (`viewResume`, :1077) is commented out upstream. */
  PreviewResume: 'PreviewResumeClientContactsClicked',
  HideBulk: 'ClientContactHideBulkClicked',
  FavoriteBulk: 'ClientContactFavoriteBulkClicked',
  /** `analyticsExportedEvent` AND `analyticsExportClickedEvent` — the same string, two fields (:241, :245). */
  Export: 'ClientContactExportClicked',
  ClientContactExported: 'ClientContactExported',
  Message: 'ClientContactMessageClicked',
  MessageBulk: 'ClientContactMessageBulkClicked',
  ExportBulk: 'ClientContactExportBulkClicked',
  /** The list's search-status events; `SearchStatusEditSearchCriteriaClicked` is shared with candidates. */
  SearchStatusViewClientContacts: 'SearchStatusViewClientContactsClicked',
  SearchStatusEditSearchCriteria: 'SearchStatusEditSearchCriteriaClicked',
  /** Declared; the bulk call that would send them (:2084) is commented out upstream. */
  ExportToCsv: 'ClientContactExportToCsvClicked',
  ExportToCsvBulk: 'ClientContactExportToCsvBulkClicked',
  // SP/extension-client-contact/extension-alert-client-contact-snippet/extension-alert-client-contact-snippet.component.ng1.ts:171-179
  ProfileScroll: 'ClientContactProfileScroll',
  SocialUrl: 'ClientContactSocialUrlClicked',
  ViewCreatedInAts: 'ViewCreatedClientContactInAtsClicked',
  GetContactDetails: 'ClientContactGetContactDetailsClicked',
  /** `FirsTime`, sic. */
  GetContactDetailsFirstTime: 'ClientContactGetContactDetailsFirsTimeClicked',
  GetContactDetailsModal: 'ClientContactGetContactDetailsModalClicked',
  CreateInAts: 'ClientContactCreateInATSClicked',
  CreateInAtsModalCreate: 'ClientContactCreateInATSModalCreateClicked',
  CreateInAtsModalUpdate: 'ClientContactCreateInATSModalUpdateClicked',
  // …/client-contact-current-company/client-contact-current-company.component.ng1.ts:58
  CompanyLink: 'ClientContactCompanyLinkClick',
  // SP/extension-client-contact/extension-alert-client-contacts/extension-alert-client-contacts-message/extension-alert-client-contacts-messages.component.ng1.ts:66
  MessageSendButton: 'ClientContactMessageSendButtonClicked',
} as const
export type ClientContactAnalyticsEvent = (typeof ClientContactAnalyticsEvent)[keyof typeof ClientContactAnalyticsEvent]

/** Events of the shortlist frame itself, shared by both entity types. */
export const ShortlistAnalyticsEvent = {
  // SP/shortlist-header/shortlist-header.component.ng1.ts:50-51
  Close: 'CloseClicked',
  PinUnpin: 'PinUnpinClicked',
  // SP/shortlist-extension-alerts/slim-header-dropdown/component/slim.header.dropdown.component.ng1.ts:74-77
  HuntersDropdown: 'HuntersDropdownClicked',
  AtsFiltersSlimHeader: 'AtsFiltersSlimHeaderClicked',
  HuntersDropdownHunter: 'HuntersDropdownHunterClicked',
  HuntersDropdownJob: 'HuntersDropdownJobClicked',
  // SP/shortlist-extension-alerts/shortlist-extension-alerts.component.ng1.ts:110
  CreateFirstAlertJob: 'CreateFirstAlertJobClicked',
  // SP/services/resolvers/base/entity-hunters-container-base.ng1.ts:382-385
  SaveAlert: 'SaveAlertClicked',
  DuplicateAlert: 'DuplicateAlertClicked',
  DuplicateLinkedinAlert: 'DuplicateLinkedinAlertClicked',
  DeleteAlert: 'DeleteAlertClicked',
  // SP/common/messages/extension-alert-entity-messages-controller.ng1.ts:148-150
  MessageSaveNewTemplate: 'ExtensionMessageSaveNewTemplateClicked',
  MessageUpdateNewTemplate: 'ExtensionMessageUpdateNewTemplateClicked',
  MessageTemplateLoaded: 'ExtensionMessageTemplateLoaded',
} as const
export type ShortlistAnalyticsEvent = (typeof ShortlistAnalyticsEvent)[keyof typeof ShortlistAnalyticsEvent]

type ExtraInfoValue = string | number | boolean | null | undefined
export type ExtraInfoObject = Record<string, ExtraInfoValue>

/**
 * `new ExtraInfoBuilder(obj).toString()` (`common/analytics/extra.info.builder.ng1.ts:4-33`): every
 * key then its value, in insertion order, joined by `@@@`. Values go through `String.concat`
 * upstream, so `undefined` becomes the text `undefined` — reproduced, not cleaned up.
 */
export function buildExtraInfo(extraInfo?: ExtraInfoObject | null): string {
  if (extraInfo == null) return ''
  const parts: string[] = []
  for (const key of Object.keys(extraInfo)) parts.push(key, String(extraInfo[key]))
  return parts.join(EXTRA_INFO_DELIMITER)
}

/**
 * The names `ShortlistSessionHost[host]` resolves to in the SPA (`SP/services/shortlist.session-info.service.ng1.ts:11-19`).
 * ⚠️ The SPA's enum stops at `Reed`; the protocol's carries the shell's 30-odd job boards too.
 * Upstream looks the shell's number up in the SPA's enum, so a shell-only host is `undefined`
 * there and reaches the wire as the text `undefined`. Reproduced: do not "fix" it with the wider enum.
 */
const SPA_SESSION_HOST_NAMES = ['None', 'ATS', 'Linkedin', 'Indeed', 'CVLibrary', 'Monster', 'Reed'] as const

/**
 * `ShortlistSessionHost[host]` as the SPA spells it — `None` when nothing answered, `undefined`
 * for a host only the shell's wider enum knows (see `SPA_SESSION_HOST_NAMES`).
 */
export function shortlistSessionHostName(host: number | undefined): string | undefined {
  return (SPA_SESSION_HOST_NAMES as readonly string[])[host ?? 0]
}

function viewModeName(viewMode: ViewMode): string | undefined {
  return (Object.keys(ViewMode) as Array<keyof typeof ViewMode>).find((name) => ViewMode[name] === viewMode)
}

/**
 * What `getExtraInfoObject` / `getAdditionalParams` read from `$state` and `ShortlistSessionInfoService`
 * — taken as plain values, because this port has no session-info store: the route knows its state
 * and the shell's `GetShortlistSessionHostInfo` answer.
 */
export interface ShortlistTrackingContext {
  /**
   * `$state.current.name.includes(ExtensionState.HunterReview)` (`shortlist.alert-tracking.service.ng1.ts:106`)
   * — the CANDIDATE hunter review (`…shortlist.hunter.review`) and states beneath it. ⚠️ FALSE on the
   * contacts hunter review: `…client-contacts.hunter.review` does not contain that string.
   */
  inSearch: boolean
  /**
   * `ShortlistSessionInfo.viaHunter` — `$state.includes(Hunter) || $state.includes(ClientContactHunter)`
   * (`shortlist.session-info.service.ng1.ts:48`): the route is under either hunter branch.
   */
  viaHunter: boolean
  /** The shell's `sessionHostInfo.host`. Upstream falls back to `None` when the shell does not answer (:113). */
  host?: number
  /** `sessionHostInfo.subHost`; sent only when truthy. */
  subHost?: string | null
  /** `currentViewMode`. `FullMode` is 0 and upstream's `!!currentViewMode` drops it — so does this. */
  viewMode?: ViewMode
}

/** The fields `getExtraInfoObject` reads off an alert entity (candidate or contact). */
interface TrackedEntityFields {
  algorithm?: unknown
  topMatch?: unknown
  enrichedRecordsAdded?: unknown
  enrichedExtraExperience?: unknown
}

/**
 * `getExtraInfoObject(alertEntity, additionalParams, currentViewMode)` + `getAdditionalParams`
 * (`shortlist.alert-tracking.service.ng1.ts:82-115`). Key order, which is data:
 * `viewMode, algoScore, topMatch, enriched, …additionalParams, inSearch, host, viaHunter, subHost`
 * — with `Object.assign`'s rule that a repeated key keeps its first position and takes the last value.
 */
export function shortlistExtraInfoObject(
  entity: object | null | undefined,
  additionalParams: ExtraInfoObject | null | undefined,
  context: ShortlistTrackingContext,
): ExtraInfoObject {
  const extraInfo: ExtraInfoObject = {}
  if (context.viewMode) extraInfo['viewMode'] = String(viewModeName(context.viewMode))
  if (entity) {
    const fields = entity as TrackedEntityFields
    if (fields.algorithm) extraInfo['algoScore'] = String(fields.algorithm)
    if (fields.topMatch) extraInfo['topMatch'] = String(fields.topMatch)
    if (fields.enrichedRecordsAdded) {
      extraInfo['enriched'] = String(
        fields.enrichedExtraExperience && (fields.enrichedRecordsAdded as number) > 0,
      )
    }
  }
  const additional: ExtraInfoObject = Object.assign({}, additionalParams ?? {})
  additional['inSearch'] = context.inSearch
  additional['host'] = shortlistSessionHostName(context.host)
  additional['viaHunter'] = context.viaHunter
  if (context.subHost) additional['subHost'] = context.subHost
  return Object.assign(extraInfo, additional)
}

/** `getExtraInfo(alertEntity, additionalParams, currentViewMode)` (`shortlist.alert-tracking.service.ng1.ts:76`) — the wire string. */
export function shortlistExtraInfo(
  entity: object | null | undefined,
  additionalParams: ExtraInfoObject | null | undefined,
  context: ShortlistTrackingContext,
): string {
  return buildExtraInfo(shortlistExtraInfoObject(entity, additionalParams, context))
}

/**
 * `getAlertIdFromUrl` (`shortlist.alert-tracking.service.ng1.ts:125`) — `parseInt` of the page's
 * `?alertId`, so `12abc` is 12 and an absent or non-numeric one is nothing.
 */
export function alertIdFromSearch(search: string): string | undefined {
  const alertId = parseInt(new URLSearchParams(search).get('alertId') ?? '', 10)
  return Number.isNaN(alertId) ? undefined : String(alertId)
}

/**
 * `$analytics.settings.pageTracking.basePath + $location.url()`. `basePath` is angulartics' default
 * `''` (nothing in careero sets it or `autoBasePath`), and with `html5Mode` under `<base href="/">`
 * `$location.url()` is path + `?search` + `#hash` — so the page's own location, which for this SPA
 * is upstream's ui-router URL verbatim, e.g. `/chrome-extension/extension/shortlist/review/candidate/profile?alertId=1&candidateId=2`.
 */
export function trackingPageUrl(location: Pick<Location, 'pathname' | 'search' | 'hash'> = window.location): string {
  return `${location.pathname}${location.search}${location.hash}`
}

/** `GuidService.generateGuid` (`common/services/guid/guid.service.ng1.ts`) — the label that ties a bulk action's rows together. */
export function generateGuid(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/** One event before the page URL is attached — `ITrackEventParams` without `category`, which is always `Shortlist` here. */
export interface ShortlistTrackRow {
  action: string
  value?: string | null
  extraInfo?: string
  label?: string
}

export interface TrackShortlistOptions {
  /** `label`. The service's own `trackSingleAction` drops it; the list components send it. */
  label?: string
  /**
   * What `value` falls back to when none is given. Default: the page's `?alertId`
   * (`shortlist.alert-tracking.service.ng1.ts:53`). The list components fall back to
   * `extensionAlert.id` instead (`getAlertIdAsValueInCaseValueIsMissing`), so pass it from there.
   */
  fallbackValue?: string | null
  /** Override the tracked page URL. Leave unset: see `trackingPageUrl`. */
  url?: string
}

function rowValue(value: number | null | undefined, fallback: string | null | undefined): string | null | undefined {
  return value != null ? String(value) : fallback
}

function currentSearch(): string {
  return typeof window === 'undefined' ? '' : window.location.search
}

function fireAndForget(send: () => Promise<void>): void {
  try {
    send().catch(() => undefined)
  } catch {
    // A tracking call must never break the action it records.
  }
}

function withPage(rows: readonly ShortlistTrackRow[], url: string): TrackEventWithUrl[] {
  return rows.map((row) => {
    const event: TrackEventWithUrl = { url, category: SHORTLIST_ANALYTICS_CATEGORY, action: row.action }
    if (row.label !== undefined) event.label = row.label
    if (row.value !== undefined) event.value = row.value
    if (row.extraInfo !== undefined) event.extraInfo = row.extraInfo
    return event
  })
}

/** `trackEventNicerParamsBulk(rows)` (`internal-analytics.ng1.ts:64`): one `POST /track/logBulk`, fire-and-forget. */
export function sendShortlistTrackRows(rows: readonly ShortlistTrackRow[], url?: string): void {
  fireAndForget(() => trackEventWithUrlBulk({ actions: withPage(rows, url ?? trackingPageUrl()) }))
}

/**
 * `trackSingleAction(action, value?, extraInfo?)` (`shortlist.alert-tracking.service.ng1.ts:34`, via
 * `sendToActionTrackingB`) — and the list components' private `trackSingleAction`, which differ only
 * in `label` and the value fallback (see `TrackShortlistOptions`). ONE row through `POST /track/logBulk`.
 */
export function trackShortlistAction(
  action: string,
  value?: number | null,
  extraInfo?: string,
  options: TrackShortlistOptions = {},
): void {
  fireAndForget(async () => {
    const fallback = 'fallbackValue' in options ? options.fallbackValue : alertIdFromSearch(currentSearch())
    const row: ShortlistTrackRow = { action, value: rowValue(value, fallback), extraInfo }
    if (options.label !== undefined) row.label = options.label
    await trackEventWithUrlBulk({ actions: withPage([row], options.url ?? trackingPageUrl()) })
  })
}

/**
 * `sendToActionTracking(action, value?, extraInfo?)` (`shortlist.alert-tracking.service.ng1.ts:60`)
 * and the direct `internalAnalytics.trackEventNicerParams({ category: SHORTLIST_ANALYTICS_CATEGORY, … })`
 * calls — ONE event through `GET /track/l`. Upstream uses this, not the bulk path, for Unfavorite,
 * the header/slim-header/alert events and the message send button; keep each event on the
 * endpoint upstream sends it to.
 */
export function trackShortlistEvent(
  action: string,
  value?: number | string | null,
  extraInfo?: string,
  options: TrackShortlistOptions = {},
): void {
  fireAndForget(async () => {
    const fallback = 'fallbackValue' in options ? options.fallbackValue : alertIdFromSearch(currentSearch())
    const event: TrackEventWithUrl = {
      url: options.url ?? trackingPageUrl(),
      category: SHORTLIST_ANALYTICS_CATEGORY,
      action,
    }
    if (options.label !== undefined) event.label = options.label
    const resolved = typeof value === 'string' ? value : rowValue(value, fallback)
    if (resolved !== undefined) event.value = resolved
    if (extraInfo !== undefined) event.extraInfo = extraInfo
    await trackEventWithUrl(event)
  })
}

export interface ShortlistBulkTrackingOptions {
  /**
   * The bulk row's value. The list components pass `extensionAlert.id`; the add-to-job service passes
   * the `alertId` it was given, which may be absent — then the value is `null` (`!!param.value ? … : null`).
   */
  alertId?: number | null
  /** `candidateCount` (candidates, add-to-job) or `clientContactCount` (contacts). */
  countKey: 'candidateCount' | 'clientContactCount'
  /** The count, when it is not `ids.length` (the add-to-job service counts `candidates`, not `candidateIds`). */
  count?: number
  /** Extra keys for the bulk row, after the count (`jobId`, `source` for add-to-job). */
  additionalParams?: ExtraInfoObject | null
  /** `this.data.candidateMap[id]` / `clientContactMap[id]` — the entity whose algo score etc. each row carries. */
  entityFor?: (id: number) => object | null | undefined
  /** Upstream's per-call `$state` / session / view mode. The add-to-job service passes no `viewMode`; the list components do. */
  context: ShortlistTrackingContext
  /** The shared label. Default: a fresh `generateGuid()`. */
  label?: string
}

/**
 * The two `logBulk` bodies `trackCandidateBulkAction` / `trackClientContactBulkAction` send
 * (`extension-alert-candidates.component.ng1.ts:1755`, `extension-alert-client-contacts.component.ng1.ts:1583`,
 * `shortlist.candidate-add-to-job.service.ng1.ts:79`): FIRST one row for the whole action
 * (`bulkEvent`, value = alert id, extraInfo = the count + `additionalParams`), THEN one row per id
 * (`singleEvent`, value = the id, extraInfo = that entity's), all sharing one GUID label.
 * "bulkIndicator - single row, singleAction - multiple rows".
 */
export function buildShortlistBulkRows(
  singleEvent: string,
  bulkEvent: string,
  ids: readonly number[],
  options: ShortlistBulkTrackingOptions,
): { indicator: ShortlistTrackRow; rows: ShortlistTrackRow[] } {
  const label = options.label ?? generateGuid()
  const counted: ExtraInfoObject = Object.assign(
    { [options.countKey]: options.count ?? ids.length },
    options.additionalParams ?? {},
  )
  const indicator: ShortlistTrackRow = {
    action: bulkEvent,
    value: options.alertId ? String(options.alertId) : null,
    extraInfo: shortlistExtraInfo(null, counted, options.context),
    label,
  }
  const rows = ids.map((id) => ({
    action: singleEvent,
    value: String(id),
    extraInfo: shortlistExtraInfo(options.entityFor?.(id), null, options.context),
    label,
  }))
  return { indicator, rows }
}

/**
 * `trackCandidateBulkAction(singleEvent, bulkEvent, ids, additionalParams?)` and its contact twin —
 * two `POST /track/logBulk` requests, the bulk row's first, as upstream sends them. Fire-and-forget.
 */
export function trackShortlistBulkAction(
  singleEvent: string,
  bulkEvent: string,
  ids: readonly number[],
  options: ShortlistBulkTrackingOptions & { url?: string },
): void {
  try {
    const url = options.url ?? trackingPageUrl()
    const { indicator, rows } = buildShortlistBulkRows(singleEvent, bulkEvent, ids, options)
    sendShortlistTrackRows([indicator], url)
    sendShortlistTrackRows(rows, url)
  } catch {
    // Never break the action being recorded.
  }
}
